/** @format */
import FormControl from '@mui/material/FormControl';
import NativeSelect from '@mui/material/NativeSelect';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import Container from '@mui/material/Container';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import PairingModelDetailsDutyTable from './pairing-model.details.duties.component';
import FilterDialog from './pairing-model.details.filters';
import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  getPairingModelAvailableModelsStart,
  setSelectedPairingModel,
  getSelectedPairingModelDataStart,
  clearSelectedPairingModelData,
} from '../../redux/pairing-model/pairing-model.actions';

import {
  selectPairingModelAvailableModels,
  selectPairingModelSelectedModel,
  selectPairingModelSelectedModelDetails,
} from '../../redux/pairing-model/pairing-model.selectors';
import * as d3 from 'd3';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
dayjs.extend(duration);
dayjs.extend(utc);

const pairingHeaders = [
  {
    id: 'idx',
    label: 'ID',
    numeric: true,
    disablePadding: false,
    filter: undefined,
  },
  {
    id: 'pairing_city',
    label: 'City',
    numeric: false,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'text',
      operations: ['contains', 'not contains', 'is', 'is not'],
      key: '',
      value: '',
    },
  },
  {
    id: 'pairing_duration',
    label: 'Duration',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'duration',
      format: 'hh:mm',
      inputUnits: 'seconds',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'pairing_date',
    label: 'Date',
    numeric: false,
    align: 'center',
    disablePadding: false,
    width: '120px',
    filter: {
      isSet: false,
      type: 'date',
      format: 'yyyy-mm-dd',
      start: dayjs().format('YYYY-MM-DD'),
      end: dayjs().format('YYYY-MM-DD'),
    },
  },
  {
    id: 'total_duty_time',
    label: 'Total Duty Time',
    numeric: true,
    disablePadding: true,
    filter: {
      isSet: false,
      type: 'duration',
      format: 'hh:mm',
      inputUnits: 'seconds',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'total_block_time',
    label: 'Total Block Time',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'duration',
      format: 'hh:mm',
      inputUnits: 'seconds',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'total_num_flights',
    label: 'Total # Flights',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'total_aircraft_swaps',
    label: 'Total # Aircraft Swaps',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'total_cost_of_duties',
    label: 'Total Cost of Duties',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'layover_city',
    label: 'Layover City',
    numeric: false,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'text',
      operations: ['contains', 'not contains', 'is', 'is not'],
      key: '',
      value: '',
    },
  },
  {
    id: 'max_duty_time',
    label: 'Max Duty Time',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'duration',
      format: 'hh:mm',
      inputUnits: 'seconds',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'max_block_time',
    label: 'Max Block Time',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'duration',
      format: 'hh:mm',
      inputUnits: 'seconds',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'max_num_flights',
    label: 'Max # Flights',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'max_cost_of_duties',
    label: 'Max Cost of Duties',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'max_aircraft_swaps',
    label: 'Max # Aircraft Swaps',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'block/day',
    label: 'Block per day',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
  {
    id: 'num_duties',
    label: '# Duties',
    numeric: true,
    disablePadding: false,
    filter: {
      isSet: false,
      type: 'numeric',
      min: 0,
      max: Infinity,
    },
  },
];

function secondsToHm(d) {
  d = Number(d);
  var h = Math.floor(d / 3600);
  var m = Math.floor((d % 3600) / 60);

  var hDisplay = h > 0 ? (h <= 9 ? '0' + h : h) + ':' : '00:';
  var mDisplay = m > 0 ? (m <= 9 ? '0' + m : m) : '00';

  return hDisplay + mDisplay;
}

function descendingComparator(a, b, orderBy) {
  let header = pairingHeaders.find((col) => col.id === orderBy);
  if (header.numeric) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  } else {
    if (b[orderBy] === null) return -1;
    if (a[orderBy] === null) return 1;
    return a[orderBy].localeCompare(b[orderBy], 'en', {
      numeric: false,
      sensitivity: 'base',
    });
  }
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    filters,
    setFilters,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'Select all',
            }}
          />
        </TableCell>
        {pairingHeaders.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={
              // headCell.numeric
              //   ? 'right'
              //   : headCell.align
              //   ? headCell.align
              //   : 'left'
              'center'
            }
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            width={headCell.width || 'auto'}
          >
            {filters?.find((filter) => filter.col.id === headCell.id) ? (
              <Tooltip title="Clear Filter">
                <IconButton
                  onClick={(event) => {
                    // setFilterDialogOpen(!filterDialogOpen);
                    //clear filter
                    let tempFilters = filters.filter(
                      (filter) => filter.col.id !== headCell.id
                    );
                    setFilters(tempFilters);
                  }}
                  size="small"
                >
                  <FilterAltIcon />
                </IconButton>
              </Tooltip>
            ) : null}

            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              <Typography
                sx={{ fontWeight: 'bold' }}
                component="span"
                variant="subtitle2"
                align="center"
              >
                {headCell.label}
              </Typography>

              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const {
    numSelected,
    setFilterDialogOpen,
    filterDialogOpen,
    handleDeleteClick,
  } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Pairings
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Unselect All">
          <IconButton onClick={handleDeleteClick}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title="Filter list">
        <IconButton
          onClick={(event) => {
            setFilterDialogOpen(!filterDialogOpen);
          }}
        >
          <FilterListIcon />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  setFilterDialogOpen: PropTypes.func.isRequired,
  filterDialogOpen: PropTypes.bool.isRequired,
  handleDeleteClick: PropTypes.func.isRequired,
};

function TableSearchBar(props) {
  const {
    columns,
    setColumns,
    filters,
    filteredPairings,
    setFilteredPairings,
    selectedModelDetails,
  } = props;
  const [searchText, setSearchText] = useState('');

  // const [originalPairings, setOriginalPairings] = useState(filteredPairings);

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleSearch = (event) => {};

  useEffect(() => {
    // setFilteredPairings(originalPairings);
    applyFilters(
      filters,
      selectedModelDetails,
      setFilteredPairings,
      setColumns,
      columns
    );
    let tempPairings = selectedModelDetails?.reduce((acc, current) => {
      if (searchText === '') return acc.concat([current]);
      let currFlightNums = [];
      current.duties.forEach((duty) => {
        duty.flights.forEach((flight) => {
          currFlightNums.push(flight.flight_number);
        });
      });

      if (currFlightNums.find((num) => num.includes(searchText))) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);

    if (filters.length > 0) {
      // console.log('filters changed', filters);
      for (let filter of filters) {
        // console.log(filter);
        switch (filter.type) {
          case 'duration':
          case 'numeric':
            let min = filter.range[0];
            let max = filter.range[1];
            tempPairings = tempPairings.filter(
              (pairing) =>
                pairing[filter.col.id] >= min && pairing[filter.col.id] <= max
            );
            break;
          case 'text':
            let value = filter.value;
            let operation = filter.operation;
            if (value === '') break;
            switch (operation) {
              case 'contains':
                tempPairings = tempPairings.filter((pairing) =>
                  pairing[filter.col.id].includes(value)
                );
                break;
              case 'not contains':
                tempPairings = tempPairings.filter(
                  (pairing) => !pairing[filter.col.id].includes(value)
                );
                break;

              case 'is':
                tempPairings = tempPairings.filter(
                  (pairing) => pairing[filter.col.id] === value
                );
                break;
              case 'is not':
                tempPairings = tempPairings.filter(
                  (pairing) => pairing[filter.col.id] !== value
                );
                break;
              default:
                break;
            }
            break;
          case 'date':
            let date = dayjs(filter.col.filter.min);
            let start = date.add(filter.range[0], 'day').startOf('day');
            let end = date.add(filter.range[1], 'day').endOf('day');
            // console.log([start, end]);
            tempPairings = tempPairings.filter((pairing) =>
              dayjs(pairing[filter.col.id])
                .startOf('day')
                .isBetween(start, end, 'day', '[]')
            );
            break;
          default:
            break;
        }
      }
    }

    // tempPairings = tempPairings.filter((pairing) =>
    //   filteredPairings.find((p) => p.idx === pairing.idx)
    // );
    setFilteredPairings(tempPairings);
  }, [searchText]);

  return (
    <FormControl
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
        p: 1,
        m: 1,
      }}
    >
      <TextField
        sx={{ m: 1, width: '50%' }}
        variant="outlined"
        label="Enter flight number or station code"
        value={searchText}
        onChange={handleSearchTextChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </FormControl>
  );
}

const PairingModelDeatilsView = () => {
  const dispatch = useDispatch();

  let availableModels = useSelector(selectPairingModelAvailableModels);
  let selectedModel = useSelector(selectPairingModelSelectedModel);
  let selectedModelDetails = useSelector(
    selectPairingModelSelectedModelDetails
  );
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filteredPairings, setFilteredPairings] = useState([]);
  const [columns, setColumns] = useState(
    pairingHeaders.filter((col) => col.filter)
  );
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('idx');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedPairings, setSelectedPairings] = useState([]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredPairings?.map((n) => n.idx);
      // setSelected(selectedModelDetails);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleDeleteClick = (event) => {
    handleSelectAllClick({ target: { checked: false } });
  };

  const handleSelection = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    let tempPairings = selectedModelDetails.filter((pairing) =>
      newSelected.includes(pairing.idx)
    );
    setSelectedPairings(tempPairings);
    setSelected(newSelected);
  };

  useEffect(() => {
    if (selectedModel) {
      dispatch(getSelectedPairingModelDataStart(selectedModel.name));
    } else {
      dispatch(clearSelectedPairingModelData());
    }
  }, [selectedModel, dispatch]);

  useEffect(() => {
    if (selectedModelDetails) {
      for (let col of columns) {
        if (col.filter) {
          if (col.filter.type === 'duration') {
            let min = d3.min(selectedModelDetails, (d) => d[col.id]);
            let max = d3.max(selectedModelDetails, (d) => d[col.id]);
            col.filter.min = min;
            col.filter.max = max;
            col.filter.selectedRange = [min, max];
          }
          if (col.filter.type === 'numeric') {
            let min = d3.min(selectedModelDetails, (d) => d[col.id]);
            let max = d3.max(selectedModelDetails, (d) => d[col.id]);
            col.filter.min = min;
            col.filter.max = max;
          }
          if (col.filter.type === 'date') {
            let min = d3.min(selectedModelDetails, (d) => d[col.id]);
            let max = d3.max(selectedModelDetails, (d) => d[col.id]);
            let dayDiff = dayjs(max).diff(dayjs(min), 'day');

            col.filter.min = min;
            col.filter.max = max;
            col.filter.selectedRange = [0, dayDiff];
            // console.log([0, dayDiff]);
          }
        }
      }
      setColumns(columns);
      setFilteredPairings(selectedModelDetails);
      setFilters([]);
      //
    }
  }, [selectedModelDetails]);

  useEffect(() => {
    if (filteredPairings) {
      applyFilters(
        filters,
        selectedModelDetails,
        setFilteredPairings,
        setColumns,
        columns
      );
    }
  }, [filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const handleChangeRemoveDuplicates = (event) => {
    setRemoveDuplicates(event.target.checked);
    if (event.target.checked) {
      let tempPairings = [...filteredPairings];
      tempPairings = tempPairings.reduce((acc, current) => {
        const x = acc.find((item) => {
          let currFlightNums = [];
          current.duties.forEach((duty) => {
            duty.flights.forEach((flight) => {
              currFlightNums.push(flight.flight_number);
            });
          });
          let itemFlightNums = [];
          item.duties.forEach((duty) => {
            duty.flights.forEach((flight) => {
              itemFlightNums.push(flight.flight_number);
            });
          });
          // console.log(currFlightNums, itemFlightNums);
          let sameFlightNums = _.isEqual(currFlightNums, itemFlightNums);
          // console.log(sameFlightNums);
          return sameFlightNums;
        });
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      }, []);
      setFilteredPairings(tempPairings);
    } else {
      setFilteredPairings(selectedModelDetails);
      setFilters([]);
    }
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredPairings?.length || 0)
      : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(
        filteredPairings?.length > 0 ? filteredPairings : [],
        getComparator(order, orderBy)
      ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, selectedModelDetails, filteredPairings]
  );

  // On Mount get available models
  useEffect(() => {
    dispatch(getPairingModelAvailableModelsStart());
  }, []);

  return (
    <Container
      maxWidth={false}
      sx={{
        minWidth: '100vw',
      }}
    >
      <FormControl
        required
        fullWidth
        sx={{
          p: '2%',
        }}
      >
        <InputLabel
          variant="standard"
          htmlFor="select-available-paring-models-controlled-native"
          size="large"
        >
          Select a model
        </InputLabel>
        <NativeSelect
          defaultValue={selectedModel ? selectedModel.name : ''}
          onChange={(event) => {
            // setSelectedModel(event.target.value);
            let model = availableModels.find(
              (m) => m.name === event.target.value
            );
            dispatch(setSelectedPairingModel(model));
          }}
          inputProps={{
            name: 'Select a model',
            id: 'select-available-paring-models-controlled-native',
          }}
        >
          <option aria-label="None" value="" />
          {availableModels.map((model) => {
            return (
              <option key={model.name + model.expiry} value={model.name}>
                {model.name}
              </option>
            );
          })}
        </NativeSelect>
      </FormControl>
      <Box sx={{ width: '100%' }}>
        <FilterDialog
          columns={columns}
          filters={filters}
          setFilters={setFilters}
          open={filterDialogOpen}
          setOpen={setFilterDialogOpen}
        />
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableSearchBar
            columns={columns}
            setColumns={setColumns}
            filters={filters}
            setFilters={setFilters}
            filteredPairings={filteredPairings}
            setFilteredPairings={setFilteredPairings}
            selectedModelDetails={selectedModelDetails}
          />
          <EnhancedTableToolbar
            numSelected={selected.length}
            setFilterDialogOpen={setFilterDialogOpen}
            filterDialogOpen={filterDialogOpen}
            handleDeleteClick={handleDeleteClick}
          />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={filteredPairings?.length || 0}
                filters={filters}
                setFilters={setFilters}
              />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const isItemSelected = isSelected(row.idx);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleSelection(event, row.idx)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.idx}
                      selected={isItemSelected}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.idx}
                      </TableCell>
                      <TableCell align="center">{row.pairing_city}</TableCell>
                      <TableCell align="center">
                        {row.pairing_duration_formatted}
                      </TableCell>
                      <TableCell align="center">{row.pairing_date}</TableCell>
                      <TableCell align="center">
                        {secondsToHm(row.total_duty_time)}
                      </TableCell>
                      <TableCell align="center">
                        {secondsToHm(row.total_block_time)}
                      </TableCell>
                      <TableCell align="center">
                        {row.total_num_flights}
                      </TableCell>
                      <TableCell align="center">
                        {row.total_aircraft_swaps}
                      </TableCell>
                      <TableCell align="center">
                        {row.total_cost_of_duties}
                      </TableCell>
                      <TableCell align="center">{row.layover_city}</TableCell>

                      <TableCell align="center">
                        {secondsToHm(row.max_duty_time)}
                      </TableCell>
                      <TableCell align="center">
                        {secondsToHm(row.max_block_time)}
                      </TableCell>
                      <TableCell align="center">
                        {row.max_num_flights}
                      </TableCell>
                      <TableCell align="center">
                        {row.max_cost_of_duties}
                      </TableCell>
                      <TableCell align="center">
                        {row.max_aircraft_swaps}
                      </TableCell>
                      <TableCell align="center">{row['block/day']}</TableCell>
                      <TableCell align="center">{row.num_duties}</TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPairings?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
        <FormControlLabel
          control={
            <Switch
              checked={removeDuplicates}
              onChange={handleChangeRemoveDuplicates}
            />
          }
          label="Remove Duplicates"
        />
      </Box>
      <PairingModelDetailsDutyTable pairings={selectedPairings} />
    </Container>
  );
};

export default PairingModelDeatilsView;
function applyFilters(
  filters,
  selectedModelDetails,
  setFilteredPairings,
  setColumns,
  columns
) {
  if (filters.length > 0) {
    // console.log('filters changed', filters);
    let tempPairings = [...selectedModelDetails];

    for (let filter of filters) {
      // console.log(filter);
      switch (filter.type) {
        case 'duration':
        case 'numeric':
          let min = filter.range[0];
          let max = filter.range[1];
          tempPairings = tempPairings.filter(
            (pairing) =>
              pairing[filter.col.id] >= min && pairing[filter.col.id] <= max
          );
          break;
        case 'text':
          let value = filter.value;
          let operation = filter.operation;
          if (value === '') break;
          switch (operation) {
            case 'contains':
              tempPairings = tempPairings.filter((pairing) =>
                pairing[filter.col.id].includes(value)
              );
              break;
            case 'not contains':
              tempPairings = tempPairings.filter(
                (pairing) => !pairing[filter.col.id].includes(value)
              );
              break;

            case 'is':
              tempPairings = tempPairings.filter(
                (pairing) => pairing[filter.col.id] === value
              );
              break;
            case 'is not':
              tempPairings = tempPairings.filter(
                (pairing) => pairing[filter.col.id] !== value
              );
              break;
            default:
              break;
          }
          break;
        case 'date':
          let date = dayjs(filter.col.filter.min);
          let start = date.add(filter.range[0], 'day').startOf('day');
          let end = date.add(filter.range[1], 'day').endOf('day');
          // console.log([start, end]);
          tempPairings = tempPairings.filter((pairing) =>
            dayjs(pairing[filter.col.id])
              .startOf('day')
              .isBetween(start, end, 'day', '[]')
          );
          break;
        default:
          break;
      }
    }
    setFilteredPairings(tempPairings);
    setColumns(
      columns.map((col) => {
        if (col.filter) {
          col.filter.isSet = filters.find((filter) => filter.col.id === col.id)
            ? true
            : false;
        }
        return col;
      })
    );
  } else {
    setFilteredPairings(selectedModelDetails);
  }
}
